import {
  FORM_FIELD_TYPE,
  normalizeValueSource,
} from 'app/pages/system/aiProcesses/AiProcessPage/tabs/formSchema/formFieldTypes';
import _ from 'lodash';

import { AiFormFieldReviewState, AiFormStatus } from 'constants/aiProcessRuns';
import { jsonParse, jsonStringify } from 'utils/json';

export const FIELD_TRIAGE_TONE = {
  GREEN: 'green',
  AMBER: 'amber',
  RED: 'red',
};

const LOW_CONFIDENCE_THRESHOLD = 0.5;

const NUMERIC_FIELD_TYPES = new Set([FORM_FIELD_TYPE.NUMBER, FORM_FIELD_TYPE.CURRENCY]);

// The B1 read model carries diffs on the form (AiFormInstanceReadModel.tableDiffs),
// keyed by fieldKey. Resolving them per field keeps the row component diff-aware
// without threading a second prop everywhere.
const resolveTableDiff = (form, fieldKey) => {
  if (!form || !fieldKey) {
    return null;
  }

  return (form.tableDiffs ?? []).find(diff => diff?.fieldKey === fieldKey) ?? null;
};

const diffRows = tableDiff => tableDiff?.rows ?? [];

// A diff row is "Unverified" when the engine could not reconcile it to a single
// loan income — unresolvedReason is set. These never triage green and are barred
// from bulk agreement.
export const isTableDiffUnverified = tableDiff =>
  diffRows(tableDiff).some(row => !!row?.unresolvedReason);

const hasMaterialVariance = tableDiff =>
  diffRows(tableDiff).some(row => row?.isMaterialDifference === true);

// Generic triage shared across all form types. Flagged ⇒ red. For numeric/currency
// fields that carry diff data, triage by variance magnitude (a material variance or
// an unresolved row ⇒ amber) rather than confidence — a high-confidence extraction
// that still disagrees materially with the loan record needs a look. Enum/decision
// and plain scalar fields keep the confidence floor. A null confidence is never
// green. Agreed/Overridden ⇒ green.
export const resolveFieldTriageTone = (field, options = {}) => {
  if (!field) {
    return FIELD_TRIAGE_TONE.AMBER;
  }

  if (field.reviewState === AiFormFieldReviewState.FLAGGED) {
    return FIELD_TRIAGE_TONE.RED;
  }

  if (
    field.reviewState === AiFormFieldReviewState.AGREED ||
    field.reviewState === AiFormFieldReviewState.OVERRIDDEN
  ) {
    return FIELD_TRIAGE_TONE.GREEN;
  }

  const { tableDiff = null, fieldType = null } = options;

  if (tableDiff && (isTableDiffUnverified(tableDiff) || hasMaterialVariance(tableDiff))) {
    return FIELD_TRIAGE_TONE.AMBER;
  }

  if (tableDiff && NUMERIC_FIELD_TYPES.has(fieldType)) {
    return FIELD_TRIAGE_TONE.GREEN;
  }

  const confidence = _.isNumber(field.confidence) ? field.confidence : null;

  if (confidence !== null && confidence >= LOW_CONFIDENCE_THRESHOLD) {
    return FIELD_TRIAGE_TONE.GREEN;
  }

  return FIELD_TRIAGE_TONE.AMBER;
};

const parseJsonValue = raw => {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  if (!_.isString(raw)) {
    return raw;
  }

  try {
    return jsonParse(raw);
  } catch {
    return raw;
  }
};

const formatScalar = (parsed, type) => {
  if (parsed === null || parsed === undefined) {
    return '—';
  }

  if (type === FORM_FIELD_TYPE.BOOLEAN) {
    return parsed ? 'Yes' : 'No';
  }

  if (_.isObject(parsed)) {
    return jsonStringify(parsed);
  }

  return String(parsed);
};

// Returns the value to display, honouring an Overridden review state (the
// override replaces the AI value). RepeatingTable yields a row array consumers
// render as a table; everything else yields a display string.
export const resolveFieldDisplay = (field, schemaField) => {
  const isOverridden = field?.reviewState === AiFormFieldReviewState.OVERRIDDEN;
  const sourceRaw = isOverridden ? field?.overriddenValue : field?.value;
  const parsed = parseJsonValue(sourceRaw);
  const type = schemaField?.type ?? FORM_FIELD_TYPE.TEXT;
  const valueSource = normalizeValueSource(schemaField?.valueSource);

  if (type === FORM_FIELD_TYPE.REPEATING_TABLE) {
    return {
      isOverridden,
      isTable: true,
      valueSource,
      rows: Array.isArray(parsed) ? parsed : [],
      text: null,
    };
  }

  return {
    isOverridden,
    isTable: false,
    valueSource,
    rows: null,
    text: formatScalar(parsed, type),
  };
};

// A field claims the full section width when it is a repeating table (always too
// wide for a half-width cell) or when it is the only field in its section (a half
// card with the other column empty reads as broken). Everything else stays in the
// two-column grid.
export const isFullWidthField = (schemaField, fieldCount) =>
  fieldCount === 1 || schemaField?.type === FORM_FIELD_TYPE.REPEATING_TABLE;

// Formats a raw (possibly JSON-encoded) field value to a display string for the
// "changed since you reviewed — was X, now Y" badge. Mirrors resolveFieldDisplay's
// scalar formatting but operates on a bare value rather than a field record.
export const formatRawValue = (raw, schemaField) =>
  formatScalar(parseJsonValue(raw), schemaField?.type ?? FORM_FIELD_TYPE.TEXT);

// The typed override widgets pre-fill from the field's current display value so
// the reviewer edits the real value rather than starting from blank. Booleans
// resolve to the stringified boolean RadioGroup expects; everything else falls
// back to the plain display string.
export const resolveOverrideInitialValue = (field, schemaField) => {
  const display = resolveFieldDisplay(field, schemaField);

  if (display.isTable) {
    return '';
  }

  if (schemaField?.type === FORM_FIELD_TYPE.BOOLEAN) {
    if (display.text === 'Yes') {
      return 'true';
    }

    if (display.text === 'No') {
      return 'false';
    }

    return '';
  }

  return display.text === '—' ? '' : display.text;
};

export const serializeOverrideValue = (rawInput, type) => {
  if (type === FORM_FIELD_TYPE.BOOLEAN) {
    return jsonStringify(rawInput === true || rawInput === 'true');
  }

  if (type === FORM_FIELD_TYPE.NUMBER || type === FORM_FIELD_TYPE.CURRENCY) {
    const numeric = Number(rawInput);

    return jsonStringify(Number.isFinite(numeric) ? numeric : null);
  }

  return jsonStringify(rawInput ?? '');
};

const TRANSITIONS_BY_STATUS = {
  [AiFormStatus.DRAFTING]: ['READY_FOR_REVIEW'],
  [AiFormStatus.NEEDS_REWORK]: ['READY_FOR_REVIEW'],
  [AiFormStatus.READY_FOR_REVIEW]: ['VALIDATE', 'APPROVE', 'SEND_BACK_FOR_REWORK'],
  [AiFormStatus.VALIDATED]: ['APPROVE', 'SEND_BACK_FOR_REWORK'],
};

export const resolveAvailableTransitions = status => TRANSITIONS_BY_STATUS[status] ?? [];

export const isFieldActionable = formStatus => formStatus === AiFormStatus.READY_FOR_REVIEW;

const collectSchemaFields = form =>
  (form?.schema?.sections ?? []).flatMap(section => section?.fields ?? []);

// Every key the form's current schema names, table columns included, lower-cased: the
// API matches field keys case-insensitively.
const collectSchemaFieldKeys = form => {
  const keys = new Set();

  const add = schemaField => {
    if (schemaField?.key) {
      keys.add(schemaField.key.toLowerCase());
    }

    (schemaField?.itemFields ?? []).forEach(add);
  };

  collectSchemaFields(form).forEach(add);

  return keys;
};

// LEN-213: a stored value whose key the process's current schema no longer has (the field
// was removed after the run filled it). It is shown read-only for the audit trail and is
// never something to review: it stays out of the pending tally, "fields need attention",
// the triage dot and bulk review, and the API does not let it block Approve. The API flags
// it (isUnmapped); an older response without the flag is judged against the schema here.
// With no schema nothing is unmapped.
const isUnmappedField = (field, form, schemaKeys = collectSchemaFieldKeys(form)) => {
  if (_.isBoolean(field?.isUnmapped)) {
    return field.isUnmapped;
  }

  if (!form?.schema || !field?.fieldKey) {
    return false;
  }

  return !schemaKeys.has(field.fieldKey.toLowerCase());
};

// The form's stored values a reviewer can act on: every value except the unmapped ones.
const collectMappedFields = form => {
  const schemaKeys = collectSchemaFieldKeys(form);

  return (form?.fields ?? []).filter(field => !isUnmappedField(field, form, schemaKeys));
};

const buildSchemaTypesByKey = form =>
  Object.fromEntries(
    collectSchemaFields(form).map(schemaField => [schemaField.key, schemaField.type]),
  );

const buildSchemaLabelsByKey = form =>
  Object.fromEntries(
    collectSchemaFields(form).map(schemaField => [
      schemaField.key,
      schemaField.label ?? schemaField.key,
    ]),
  );

// Triage tone for a field in the context of its form — wires in the form's diff
// data and the field's schema type so numeric/currency variance triage applies.
// Used by the per-form roll-ups; the row component resolves its own diff inline.
const resolveFieldTriageToneInForm = (field, form, schemaTypesByKey) =>
  resolveFieldTriageTone(field, {
    tableDiff: resolveTableDiff(form, field?.fieldKey),
    fieldType: schemaTypesByKey[field?.fieldKey] ?? null,
  });

// Total fields the schema expects versus the ones the AI has actually populated.
// "Filled" keys off a non-empty value on the live/persisted field record so the
// progress strip advances as form_field_set events stream in during Drafting.
export const countSchemaFields = form => collectSchemaFields(form).length;

export const countFilledFields = form => {
  const fieldsByKey = Object.fromEntries(
    (form?.fields ?? []).map(field => [field.fieldKey, field]),
  );

  return collectSchemaFields(form).reduce((count, schemaField) => {
    const field = fieldsByKey[schemaField.key];
    const value = field?.value;
    const isFilled = !_.isNil(value) && value !== '';

    return isFilled ? count + 1 : count;
  }, 0);
};

// Fields a reviewer should look at: amber (pending/low-confidence) or red
// (flagged). Only counts fields the AI has populated — an unfilled schema slot
// is "not yet drafted", not "needs attention".
export const countFieldsNeedingAttention = form => {
  const schemaTypesByKey = buildSchemaTypesByKey(form);

  return collectMappedFields(form).reduce((count, field) => {
    const tone = resolveFieldTriageToneInForm(field, form, schemaTypesByKey);

    return tone === FIELD_TRIAGE_TONE.AMBER || tone === FIELD_TRIAGE_TONE.RED ? count + 1 : count;
  }, 0);
};

const REVIEW_STATE_KEYS = {
  [AiFormFieldReviewState.AGREED]: 'agreed',
  [AiFormFieldReviewState.OVERRIDDEN]: 'overridden',
  [AiFormFieldReviewState.FLAGGED]: 'flagged',
  [AiFormFieldReviewState.PENDING]: 'pending',
};

// Pre-sign-off tally over the form's fields (unmapped values left out), keyed by review state. A field with
// no recorded reviewState (never touched) counts as still-Pending so the gate
// can call out un-reviewed slots. Drift (changedAfterReview) is surfaced too so
// the confirm step can warn that a signed-off field moved underneath the user.
export const summarizeFormReview = form => {
  const fields = collectMappedFields(form);

  const counts = fields.reduce(
    (acc, field) => {
      const key = REVIEW_STATE_KEYS[field?.reviewState] ?? 'pending';

      acc[key] += 1;

      if (field?.changedAfterReview === true) {
        acc.changedAfterReview += 1;
      }

      return acc;
    },
    { agreed: 0, overridden: 0, flagged: 0, pending: 0, changedAfterReview: 0 },
  );

  const hasBlockingFields = counts.pending > 0 || counts.flagged > 0;

  return { ...counts, hasBlockingFields };
};

// Filled fields whose fieldKey is no longer present in any schema section. These
// would otherwise be dropped entirely on a drifted schema — surfacing them keeps
// AI output visible (read-only) rather than silently discarded.
export const collectUnmappedFields = form => {
  const schemaKeys = collectSchemaFieldKeys(form);

  return (form?.fields ?? []).filter(
    field => field?.fieldKey && isUnmappedField(field, form, schemaKeys),
  );
};

const TRIAGE_TONE_SEVERITY = {
  [FIELD_TRIAGE_TONE.GREEN]: 0,
  [FIELD_TRIAGE_TONE.AMBER]: 1,
  [FIELD_TRIAGE_TONE.RED]: 2,
};

// Worst triage tone across a form's fields — drives the per-run triage dot. A
// form with no populated fields yet has nothing to triage, so returns null.
export const resolveWorstFieldTriageTone = form => {
  const fields = collectMappedFields(form);

  if (fields.length === 0) {
    return null;
  }

  const schemaTypesByKey = buildSchemaTypesByKey(form);

  return fields.reduce((worst, field) => {
    const tone = resolveFieldTriageToneInForm(field, form, schemaTypesByKey);

    if (worst === null || TRIAGE_TONE_SEVERITY[tone] > TRIAGE_TONE_SEVERITY[worst]) {
      return tone;
    }

    return worst;
  }, null);
};

const BULK_EXCLUSION_REASON = {
  ALREADY_REVIEWED: 'alreadyReviewed',
  FLAGGED: 'flagged',
  UNVERIFIED: 'unverified',
  HIGH_VARIANCE: 'highVariance',
  NULL_CONFIDENCE: 'nullConfidence',
  SCHEMA_DRIFT: 'schemaDrift',
};

// Partitions the form's filled fields into the set the reviewer can safely agree
// to in one click ("low-risk") and the set held back, each with the reason it was
// excluded. Low-risk excludes: anything already reviewed (not Pending), Flagged,
// Unverified (unresolved diff rows), material-variance numeric fields, null/absent
// confidence, and — while the schema has drifted — every field. The caller shows
// the eligible count plus the excluded list before committing.
const resolveBulkExclusionReason = (field, form, isSchemaDrifted) => {
  if (isSchemaDrifted) {
    return BULK_EXCLUSION_REASON.SCHEMA_DRIFT;
  }

  if ((field.reviewState ?? AiFormFieldReviewState.PENDING) !== AiFormFieldReviewState.PENDING) {
    return BULK_EXCLUSION_REASON.ALREADY_REVIEWED;
  }

  const tableDiff = resolveTableDiff(form, field.fieldKey);

  if (isTableDiffUnverified(tableDiff)) {
    return BULK_EXCLUSION_REASON.UNVERIFIED;
  }

  if (hasMaterialVariance(tableDiff)) {
    return BULK_EXCLUSION_REASON.HIGH_VARIANCE;
  }

  if (!_.isNumber(field.confidence)) {
    return BULK_EXCLUSION_REASON.NULL_CONFIDENCE;
  }

  return null;
};

export const resolveBulkEligibility = form => {
  const schemaLabelsByKey = buildSchemaLabelsByKey(form);
  const isSchemaDrifted = form?.schemaVersionMatches === false;

  const eligible = [];
  const excluded = [];

  collectMappedFields(form).forEach(field => {
    if (!field?.fieldKey) {
      return;
    }

    const reason = resolveBulkExclusionReason(field, form, isSchemaDrifted);

    if (reason === null) {
      eligible.push({ fieldKey: field.fieldKey });

      return;
    }

    if (reason === BULK_EXCLUSION_REASON.ALREADY_REVIEWED) {
      return;
    }

    excluded.push({
      fieldKey: field.fieldKey,
      label: schemaLabelsByKey[field.fieldKey] ?? field.fieldKey,
      reason,
    });
  });

  return { eligible, excluded };
};

const TRIAGE_SORT_RANK = {
  flagged: 0,
  varianceFlagged: 1,
  lowOrNullConfidence: 2,
  pending: 3,
  done: 4,
};

// Per-field review-urgency rank used to sort the surface: Flagged first, then
// variance/unverified-flagged numeric diffs, then low/null-confidence pending
// fields, then the remaining Pending slots, then already-reviewed (done) fields.
export const resolveFieldSortRank = (field, form) => {
  if (field?.reviewState === AiFormFieldReviewState.FLAGGED) {
    return TRIAGE_SORT_RANK.flagged;
  }

  if (
    field?.reviewState === AiFormFieldReviewState.AGREED ||
    field?.reviewState === AiFormFieldReviewState.OVERRIDDEN
  ) {
    return TRIAGE_SORT_RANK.done;
  }

  const tableDiff = resolveTableDiff(form, field?.fieldKey);

  if (tableDiff && (isTableDiffUnverified(tableDiff) || hasMaterialVariance(tableDiff))) {
    return TRIAGE_SORT_RANK.varianceFlagged;
  }

  const confidence = _.isNumber(field?.confidence) ? field.confidence : null;

  if (confidence === null || confidence < LOW_CONFIDENCE_THRESHOLD) {
    return TRIAGE_SORT_RANK.lowOrNullConfidence;
  }

  return TRIAGE_SORT_RANK.pending;
};
